import { useEffect, useMemo, useRef, useState } from 'react';
import { tempoMapOf } from '@/domain/selectors';
import { formatBarBeat, signatureAt, ticksPerBar } from '@/timing/tempoMap';
import { signatureMapOf } from '@/domain/selectors';
import { snapTick } from '@/timing/snap';
import { command } from '@/commands/registry';
import * as TransportCommands from '@/commands/transportCommands';
import { getServices } from '@/state/services';
import { useDaw } from '@/state/store';

/**
 * The tempo map, drawn.
 *
 * Tempo changes could already be typed into the transport one at a time, which
 * is fine for "this piece is at 96" and hopeless for shaping a ritardando: the
 * thing being edited is a curve, and a curve is edited by looking at it.
 *
 * The lane draws the same map the timing engine uses — including the exact shape
 * of a ramp, which is a logarithm rather than a straight line, so what is drawn
 * is what will be heard rather than an approximation of it.
 */

/** The visible tempo range. Wide enough for anything musical, tight enough to aim in. */
const MIN_BPM = 40;
const MAX_BPM = 240;

export function TempoLane(): JSX.Element {
  const project = useDaw((s) => s.project);
  const dispatch = useDaw((s) => s.dispatch);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ width: 800, height: 120 });
  const dragRef = useRef<{ tick: number } | null>(null);

  const map = useMemo(() => tempoMapOf(project), [project]);
  const sigMap = useMemo(() => signatureMapOf(project), [project]);
  const ppq = project.transport.ppq;

  // The lane always shows the whole piece: a tempo map is short, and scrolling
  // it independently of the arrangement would be one more thing to keep in step.
  const lastTick = Math.max(
    ppq * 16,
    ...project.transport.tempoMap.map((event) => event.tick),
    ...Object.values(project.clips).map((clip) => clip.startTicks),
  );
  const spanTicks = lastTick + ticksPerBar(signatureAt(sigMap, lastTick), ppq) * 2;

  const xOf = (tick: number): number => (tick / spanTicks) * size.width;
  const tickOf = (x: number): number => Math.max(0, Math.round((x / size.width) * spanTicks));
  const yOf = (bpm: number): number => {
    const clamped = Math.min(MAX_BPM, Math.max(MIN_BPM, bpm));
    // Logarithmic, so halving and doubling the tempo are the same distance.
    const progress = Math.log2(clamped / MIN_BPM) / Math.log2(MAX_BPM / MIN_BPM);
    return size.height - progress * (size.height - 16) - 8;
  };
  const bpmOf = (y: number): number => {
    const progress = (size.height - 8 - y) / (size.height - 16);
    return MIN_BPM * Math.pow(MAX_BPM / MIN_BPM, Math.min(1, Math.max(0, progress)));
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const observer = new ResizeObserver(() => {
      setSize({ width: canvas.clientWidth, height: canvas.clientHeight });
    });
    observer.observe(canvas);
    setSize({ width: canvas.clientWidth, height: canvas.clientHeight });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(size.width * dpr);
    canvas.height = Math.round(size.height * dpr);
    ctx.save();
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, size.width, size.height);
    ctx.fillStyle = '#0b0d11';
    ctx.fillRect(0, 0, size.width, size.height);

    // Bar lines, so a tempo change can be read against the music.
    ctx.font = '9px ui-monospace, monospace';
    const barTicks = ticksPerBar(signatureAt(sigMap, 0), ppq);
    for (let tick = 0; tick <= spanTicks; tick += barTicks) {
      const x = Math.round(xOf(tick)) + 0.5;
      ctx.fillStyle = '#171b22';
      ctx.fillRect(x, 0, 1, size.height);
    }
    for (const bpm of [60, 90, 120, 180]) {
      const y = Math.round(yOf(bpm)) + 0.5;
      ctx.fillStyle = '#1b2029';
      ctx.fillRect(0, y, size.width, 1);
      ctx.fillStyle = '#4b5563';
      ctx.fillText(String(bpm), 3, y - 3);
    }

    // The curve itself, sampled finely enough that a ramp's curvature shows.
    ctx.strokeStyle = '#7fb0ff';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    const steps = Math.max(64, Math.round(size.width));
    for (let i = 0; i <= steps; i++) {
      const tick = (i / steps) * spanTicks;
      const bpm = tempoAtTick(map, tick);
      const x = xOf(tick);
      const y = yOf(bpm);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();

    for (const event of map.events) {
      const x = xOf(event.tick);
      const y = yOf(event.bpm);
      ctx.fillStyle = event.ramp ? '#ffd27f' : '#cfe0ff';
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }, [map, sigMap, size, spanTicks, ppq, project]);

  /** The tempo event nearest a position, if the pointer is close enough to grab it. */
  const eventAt = (x: number, y: number): number | null => {
    for (const event of map.events) {
      if (Math.abs(xOf(event.tick) - x) <= 6 && Math.abs(yOf(event.bpm) - y) <= 8) return event.tick;
    }
    return null;
  };

  const positionOf = (e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  return (
    <div className="tempo-lane">
      <canvas
        ref={canvasRef}
        className="tempo-lane__canvas"
        aria-label="Tempo curve"
        onPointerDown={(e) => {
          const { x, y } = positionOf(e);
          const existing = eventAt(x, y);

          if (existing !== null && e.altKey) {
            dispatch(command(TransportCommands.removeTempo, { tick: existing }));
            return;
          }
          if (existing !== null) {
            e.currentTarget.setPointerCapture(e.pointerId);
            dragRef.current = { tick: existing };
            return;
          }
          if (e.altKey) return;

          // A new change lands on the user's own snap grid, as one typed into
          // the transport does.
          const tick = Math.max(1, snapTick(sigMap, project.transport.snap, tickOf(x)));
          dispatch(command(TransportCommands.setTempo, { tick, bpm: bpmOf(y) }));
          e.currentTarget.setPointerCapture(e.pointerId);
          dragRef.current = { tick };
        }}
        onPointerMove={(e) => {
          const drag = dragRef.current;
          if (!drag) return;
          const { y } = positionOf(e);
          dispatch(command(TransportCommands.setTempo, { tick: drag.tick, bpm: bpmOf(y) }));
        }}
        onPointerUp={(e) => {
          dragRef.current = null;
          e.currentTarget.releasePointerCapture(e.pointerId);
          getServices().commands.breakCoalescing();
        }}
      />
      <div className="tempo-lane__events">
        {map.events.map((event, index) => (
          <span key={event.tick} className="tempo-lane__event">
            <span className="tempo-lane__at">{formatBarBeat(sigMap, event.tick)}</span>
            <span className="tempo-lane__bpm">{event.bpm.toFixed(2)}</span>
            <button
              className={`btn btn--ghost btn--icon ${event.ramp ? 'btn--active' : ''}`}
              aria-label={`${event.ramp ? 'Hold' : 'Ramp'} the tempo from ${formatBarBeat(sigMap, event.tick)}`}
              title={
                index === map.events.length - 1
                  ? 'Nothing to ramp towards: this is the last change.'
                  : event.ramp
                    ? 'Ramping to the next change. Click to hold instead.'
                    : 'Holding until the next change. Click to ramp instead.'
              }
              disabled={index === map.events.length - 1}
              onClick={() =>
                dispatch(
                  command(TransportCommands.setTempoRamp, { tick: event.tick, ramp: !event.ramp, shape: 'linear' as const }),
                )
              }
            >
              {event.ramp ? '↗' : '→'}
            </button>
            {event.ramp && index < map.events.length - 1 && (
              <button
                className={`btn btn--ghost btn--icon ${event.rampShape === 'exponential' ? 'btn--active' : ''}`}
                aria-label={`${event.rampShape === 'exponential' ? 'Straight' : 'Even'} ramp from ${formatBarBeat(sigMap, event.tick)}`}
                title={
                  event.rampShape === 'exponential'
                    ? 'Even: each beat changes by the same proportion, which is how a ritardando sounds. Click for the same BPM per beat instead.'
                    : 'Straight: the same BPM added every beat. Click for an even ramp, which changes each beat by the same proportion.'
                }
                onClick={() =>
                  dispatch(
                    command(TransportCommands.setTempoRamp, {
                      tick: event.tick,
                      ramp: true,
                      shape: event.rampShape === 'exponential' ? ('linear' as const) : ('exponential' as const),
                    }),
                  )
                }
              >
                {event.rampShape === 'exponential' ? '∿' : '／'}
              </button>
            )}
            {event.tick > 0 && (
              <button
                className="btn btn--ghost btn--icon"
                aria-label={`Remove the tempo change at ${formatBarBeat(sigMap, event.tick)}`}
                onClick={() => dispatch(command(TransportCommands.removeTempo, { tick: event.tick }))}
              >
                {'−'}
              </button>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

/** The tempo at a tick, including inside a ramp. */
function tempoAtTick(map: ReturnType<typeof tempoMapOf>, tick: number): number {
  const events = map.events;
  let index = 0;
  for (let i = 0; i < events.length; i++) {
    if (events[i].tick <= tick) index = i;
    else break;
  }
  const event = events[index];
  const next = events[index + 1];
  if (!event.ramp || !next) return event.bpm;
  const span = next.tick - event.tick;
  const progress = span <= 0 ? 0 : Math.min(1, Math.max(0, (tick - event.tick) / span));
  return event.bpm + (next.bpm - event.bpm) * progress;
}
